import BaseAuthProvider from './BaseAuthProvider'
import AuthService from '../service'
import { AuthProviders } from '../constants/authProviders'

/**
 * Email Token Authentication Provider (Passwordless)
 * Two-step authentication:
 * 1. User enters email → receives 4-digit code via email
 * 2. User enters code → authenticated
 */
export class EmailTokenAuthProvider extends BaseAuthProvider {
    getMethod() {
        return AuthProviders.EMAIL_TOKEN
    }

    /**
     * Email token login is controlled only by .env
     * No custom subdomain or backend restrictions
     */
    isEnabled(context) {
        return context.envProviders.includes(this.getMethod())
    }

    /**
     * Request login - check if user exists and send token
     * This is step 1 of the passwordless flow
     * @param {string} email - User email address
     * @returns {Promise<{status: string, email: string}>}
     */
    async requestToken(email) {
        const response = await AuthService.requestLogin(email)
        return response.data
    }

    /**
     * Register new user and send token
     * Used for new users in the passwordless flow
     * @param {Object} data - Registration data
     * @returns {Promise<{status: string, email: string}>}
     */
    async registerAndSendToken(data) {
        const response = await AuthService.registerUser(data)
        return response.data
    }

    /**
     * Verify email token and authenticate
     * This is step 2 of the passwordless flow
     * @param {Object} credentials
     * @param {string} credentials.email - User email
     * @param {string} credentials.token - 4-digit verification code
     * @returns {Promise<{token: string, tokenType: string}>}
     */
    async authenticate({ email, token }) {
        const response = await AuthService.verifyToken(email, token, {
            include_perms: false,
            include_groups: false,
            include_jwt: false
        })

        // Return token plus any additional metadata from backend
        return {
            token: response.data.token,
            tokenType: 'Token',
            ...response.data  // Include metadata if backend adds any in future
        }
    }

    /**
     * Suggest alternatives if user wants different login method
     */
    getAlternatives(enabledProviders) {
        const alternatives = []

        if (enabledProviders.includes(AuthProviders.PASSWORD)) {
            alternatives.push(AuthProviders.PASSWORD)
        }

        return alternatives
    }
}

export default EmailTokenAuthProvider
